/**
 * Agentic Staff Otto — shared contract.
 *
 * Staff Otto can run "skills" that gather CRM data and pre-fill a creation
 * form (quote / invoice / email campaign) ready for a human to review + save.
 * This file is the single source of truth for the wire shapes exchanged
 * between three parties:
 *
 *   1. the AI gateway  (xop-brain `POST /agent/act`, Python) — mirrors these
 *      shapes in its own code; it streams the SSE event union below and
 *      declares the data + terminal tools;
 *   2. the dashboard server proxy (`apps/server` `POST /api/otto/act`) — mints
 *      the staff EdDSA token and streams the gateway SSE back to the browser;
 *   3. the dashboard web agent surface (`apps/web/src/otto-agent`) — owns the
 *      transcript, renders the thinking, executes the *client* data tools
 *      against crm-api, and applies the terminal `action`.
 *
 * STAFF-ONLY. Nothing here is reachable from the customer chat widget: the
 * gateway hard-gates `/agent/act` to a staff bot + staff `user_role`, and the
 * only UI that speaks this protocol lives in the staff dashboard.
 *
 * The gateway is STATELESS: the browser sends the full {@link OttoTranscript}
 * on every turn (including the assistant `tool_use` blocks and the
 * `tool_result` blocks it produced), so the gateway never stores conversation
 * state. See `docs/otto-agent.md`.
 */
import type { Permission } from "./permissions.js";

/** The v1 skills. Extend this list (+ its permission map + the gateway's
 * terminal tools) to teach Otto a new action. */
export const OTTO_AGENT_SKILLS = [
  "create_invoice",
  "create_quote",
  "create_campaign",
] as const;

export type OttoAgentSkillId = (typeof OTTO_AGENT_SKILLS)[number];

/**
 * The CRM permission(s) the *acting staff user* must hold for each skill.
 * Invoices/quotes live behind the financial wall (the generate routes sit in
 * `RequireFinancialOutlet`), so those skills need `crm:financials` on top of
 * `crm:view`; a campaign only needs CRM entry. The dashboard filters the
 * offered skills by this map AND tells the gateway which are enabled, so the
 * model can never drive a skill the user isn't allowed to perform.
 */
export const OTTO_SKILL_PERMISSIONS: Record<
  OttoAgentSkillId,
  readonly Permission[]
> = {
  create_invoice: ["crm:view", "crm:financials"],
  create_quote: ["crm:view", "crm:financials"],
  create_campaign: ["crm:view"],
};

/** Human-readable skill labels (shown in the launcher). */
export const OTTO_SKILL_LABELS: Record<OttoAgentSkillId, string> = {
  create_invoice: "Draft an invoice",
  create_quote: "Draft a quote",
  create_campaign: "Draft an email campaign",
};

/**
 * The data tools the gateway may ask the *browser* to execute (it holds the
 * scoped `xop_crm_token`, so crm-api enforces the financial / reseller /
 * view-scope walls automatically). The gateway emits a {@link OttoToolCallEvent}
 * and pauses; the client runs the tool via `crm/lib/otto-tools.ts` and
 * re-invokes `/agent/act` with the result appended to the transcript.
 */
export const OTTO_DATA_TOOLS = [
  "find_customer",
  "list_customer_contacts",
  "get_customer_deals",
  "search_products",
  "list_contact_groups",
  "search_contacts",
] as const;

export type OttoDataToolName = (typeof OTTO_DATA_TOOLS)[number];

/** Terminal actions the gateway emits when a skill has gathered everything it
 * needs. The client validates the draft and hands off to the matching form. */
export type OttoActionKind =
  | "prefill_invoice"
  | "prefill_quote"
  | "prefill_campaign";

/** Map a skill to the terminal action it produces. */
export const OTTO_SKILL_ACTION: Record<OttoAgentSkillId, OttoActionKind> = {
  create_invoice: "prefill_invoice",
  create_quote: "prefill_quote",
  create_campaign: "prefill_campaign",
};

/**
 * Anthropic-shaped content blocks. The browser owns the transcript and rebuilds
 * it from the `assistant` events + the `tool_result`s it produces, so the
 * gateway can stay stateless.
 */
export type OttoContentBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { type: "tool_result"; tool_use_id: string; content: string }
  // The Claude models that always think (Opus 5.5, Fable) must get a turn's
  // thinking back with its tool calls. The browser never reads these — it
  // stores the `assistant` event's blocks as-is and replays them (2026-09-25).
  | { type: "thinking"; thinking: string; signature: string }
  | { type: "redacted_thinking"; data: string };

export interface OttoTranscriptMessage {
  role: "user" | "assistant";
  content: OttoContentBlock[];
}

export type OttoTranscript = OttoTranscriptMessage[];

/** Request body for `POST /api/otto/act` (and the gateway `/agent/act`). */
export interface OttoActRequest {
  /** The full conversation so far — the client owns this (stateless gateway). */
  transcript: OttoTranscript;
  /** Skills the acting user is permitted to run (already permission-filtered). */
  enabled_skills: OttoAgentSkillId[];
  /** Non-authoritative hints about the actor for prompt framing. Authority is
   * the signed token (server) + crm-api (tool calls), never this. */
  actor?: {
    role?: string;
    name?: string;
    can_financials?: boolean;
    can_view_all?: boolean;
  };
}

/* ── SSE event union (one JSON object per `data:` line) ─────────────────── */

/** The model's streamed reasoning narration — the visible "thinking". */
export interface OttoThinkingEvent {
  type: "thinking";
  text: string;
}
/** A human-readable step marker for the timeline HUD. */
export interface OttoStepEvent {
  type: "step";
  label: string;
  status: "start" | "done" | "error";
}
/** The gateway wants the browser to run a data tool, then re-invoke. */
export interface OttoToolCallEvent {
  type: "tool_call";
  id: string;
  name: OttoDataToolName;
  input: Record<string, unknown>;
}
/** The raw assistant blocks for this turn, so the client can append them to the
 * transcript before executing tools / the next turn. */
export interface OttoAssistantEvent {
  type: "assistant";
  blocks: OttoContentBlock[];
}
/** Terminal: a fully-formed draft to apply (cloak → navigate → prefill). */
export interface OttoActionEvent {
  type: "action";
  kind: OttoActionKind;
  /** Shape depends on `kind`; validated by the CRM-side applier (kept `unknown`
   * here so the shared package never imports CRM internals). */
  draft: unknown;
}
/** A plain conversational reply / clarifying question (no action). */
export interface OttoMessageEvent {
  type: "message";
  text: string;
}
export interface OttoDoneEvent {
  type: "done";
}
export interface OttoErrorEvent {
  type: "error";
  message: string;
}

export type OttoAgentEvent =
  | OttoThinkingEvent
  | OttoStepEvent
  | OttoToolCallEvent
  | OttoAssistantEvent
  | OttoActionEvent
  | OttoMessageEvent
  | OttoDoneEvent
  | OttoErrorEvent;

/** Narrow an unknown parsed SSE payload to an {@link OttoAgentEvent}. Returns
 * `null` for anything unrecognised (defensive against a malformed frame). */
export function parseOttoAgentEvent(value: unknown): OttoAgentEvent | null {
  if (!value || typeof value !== "object") return null;
  const type = (value as { type?: unknown }).type;
  switch (type) {
    case "thinking":
    case "step":
    case "tool_call":
    case "assistant":
    case "action":
    case "message":
    case "done":
    case "error":
      return value as OttoAgentEvent;
    default:
      return null;
  }
}
